import React, { useRef } from 'react';
import { FiPlay, FiPause, FiVolume2, FiVolumeX, FiMinus, FiX, FiDownload } from 'react-icons/fi';
import { useAudio } from '../contexts/AudioContext';
import AudioUrlService from '../services/AudioUrlService';
import './AudioPlayer.css';

const GEN_ICONS = { podcast: '🎙️', audiobook: '📖' };

const AudioPlayer = () => {
  const {
    currentAudio,
    isPlaying,
    progress,
    duration,
    volume,
    playbackRate,
    isMinimized,
    isLoading,
    error,
    togglePlayPause,
    seekTo,
    setVolume,
    setPlaybackRate,
    clearAudio,
    toggleMinimize,
    generationJob,
    clearGeneration,
  } = useAudio();

  const progressBarRef = useRef(null);

  // Nothing to show: no playback AND no generation
  if (!currentAudio && !generationJob) return null;

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e) => {
    if (!progressBarRef.current || !duration) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const percentage = (e.clientX - rect.left) / rect.width;
    seekTo(percentage * duration);
  };

  const handleDownload = async () => {
    try {
      if (!currentAudio.documentId) return;
      // Get a signed URL with Content-Disposition: attachment from the backend
      const downloadUrl = await AudioUrlService.fetchDownloadUrl(currentAudio.documentId);
      // Navigate to the signed URL — browser downloads due to Content-Disposition header
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${currentAudio.title || 'audio'}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const cyclePlaybackRate = () => {
    const rates = [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0];
    const idx = rates.findIndex(r => Math.abs(r - playbackRate) < 0.01);
    setPlaybackRate(rates[(idx + 1) % rates.length]);
  };

  const handleVolumeChange = (e) => {
    setVolume(parseFloat(e.target.value));
  };

  const isMuted = volume === 0;

  const isGenerating = generationJob && generationJob.status === 'generating';
  const genFailed = generationJob && generationJob.status === 'failed';
  const genComplete = generationJob && generationJob.status === 'complete';

  // Generation-only view (no audio is playing yet)
  if (!currentAudio && generationJob) {
    return (
      <div className="audio-player ap-gen-only">
        <div className="ap-gen-bar">
          <span className="ap-gen-icon">
            {isGenerating && <span className="ap-gen-spinner" />}
            {genFailed && <span className="ap-gen-fail-icon">✕</span>}
            {genComplete && <span>✓</span>}
            {' '}{GEN_ICONS[generationJob.toolType] || '🔊'}
          </span>
          <div className="ap-gen-info">
            <div className="ap-gen-title">{generationJob.title || `Generating ${generationJob.toolType}…`}</div>
            {isGenerating && (
              <div className="ap-gen-stage">{generationJob.stage || 'Starting…'}</div>
            )}
            {genFailed && (
              <div className="ap-gen-error">{generationJob.error || 'Generation failed'}</div>
            )}
            {genComplete && (
              <div className="ap-gen-stage">Complete!</div>
            )}
          </div>
          {(genFailed || genComplete) && (
            <button className="ap-icon-btn" onClick={clearGeneration} title="Dismiss">
              <FiX size={14} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`audio-player ${isMinimized ? 'minimized' : ''} ${isPlaying ? 'playing' : ''}`}>
      {/* Generation indicator bar — shown above the player when generating alongside playback */}
      {generationJob && !isMinimized && (
        <div className="ap-gen-bar">
          <span className="ap-gen-icon">
            {isGenerating && <span className="ap-gen-spinner" />}
            {genFailed && <span className="ap-gen-fail-icon">✕</span>}
            {genComplete && <span>✓</span>}
            {' '}{GEN_ICONS[generationJob.toolType] || '🔊'}
          </span>
          <div className="ap-gen-info">
            <div className="ap-gen-title">{generationJob.title || `Generating ${generationJob.toolType}…`}</div>
            {isGenerating && (
              <div className="ap-gen-stage">{generationJob.stage || 'Starting…'}</div>
            )}
            {genFailed && (
              <div className="ap-gen-error">{generationJob.error || 'Generation failed'}</div>
            )}
            {genComplete && (
              <div className="ap-gen-stage">Complete!</div>
            )}
          </div>
          {(genFailed || genComplete) && (
            <button className="ap-icon-btn" onClick={clearGeneration} title="Dismiss">
              <FiX size={14} />
            </button>
          )}
        </div>
      )}

      {!isMinimized && currentAudio && (
        <>
          {/* Header */}
          <div className="ap-header">
            <div className="ap-info">
              <div className="ap-title">{currentAudio.title}</div>
              <div className="ap-format">
                {currentAudio.format === 'audiobook' ? '📖 Audiobook' : '🎙️ Podcast'}
              </div>
            </div>
            <div className="ap-header-btns">
              <button className="ap-icon-btn" onClick={toggleMinimize} title="Minimize">
                <FiMinus size={14} />
              </button>
              <button className="ap-icon-btn" onClick={clearAudio} title="Close">
                <FiX size={14} />
              </button>
            </div>
          </div>

          {error && <div className="ap-error">{error}</div>}

          {/* Body — play button + progress (like WaveformPlayer) */}
          <div className="ap-body">
            <button
              className="ap-play-btn"
              onClick={togglePlayPause}
              disabled={isLoading}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isLoading ? '⏳' : isPlaying ? <FiPause size={18} /> : <FiPlay size={18} />}
            </button>

            <div className="ap-center">
              <div
                ref={progressBarRef}
                className="ap-progress-bar"
                onClick={handleProgressClick}
              >
                <div
                  className="ap-progress-fill"
                  style={{ width: `${duration > 0 ? (progress / duration) * 100 : 0}%` }}
                />
              </div>
              <div className="ap-time-row">
                <span>{formatTime(progress)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
          </div>

          {/* Controls row */}
          <div className="ap-controls">
            <button className="ap-ctrl-btn" onClick={cyclePlaybackRate} title="Playback Speed">
              {playbackRate}×
            </button>
            <button className="ap-ctrl-btn" onClick={handleDownload} title="Download">
              <FiDownload size={12} />
            </button>

            <div className="ap-volume">
              <button
                className="ap-vol-btn"
                onClick={() => setVolume(isMuted ? 0.8 : 0)}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <FiVolumeX size={16} /> : <FiVolume2 size={16} />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={handleVolumeChange}
                className="ap-vol-slider"
                aria-label="Volume"
              />
            </div>
          </div>
        </>
      )}

      {isMinimized && currentAudio && (
        <div className="ap-minimized" onClick={toggleMinimize}>
          <button
            className="ap-mini-play"
            onClick={(e) => { e.stopPropagation(); togglePlayPause(); }}
          >
            {isPlaying ? <FiPause size={14} /> : <FiPlay size={14} />}
          </button>
          <span className="ap-mini-title">
            {isGenerating && <span className="ap-gen-spinner ap-gen-spinner--mini" />}
            {currentAudio.title}
          </span>
          <button
            className="ap-mini-close"
            onClick={(e) => { e.stopPropagation(); clearAudio(); }}
            title="Close"
          >
            <FiX size={14} />
          </button>
        </div>
      )}

      {/* Minimized: generation-only (no audio playing) */}
      {isMinimized && !currentAudio && generationJob && (
        <div className="ap-minimized ap-gen-minimized">
          <span className="ap-gen-spinner ap-gen-spinner--mini" />
          <span className="ap-mini-title">{generationJob.title || 'Generating…'}</span>
          {(genFailed || genComplete) && (
            <button
              className="ap-mini-close"
              onClick={(e) => { e.stopPropagation(); clearGeneration(); }}
              title="Dismiss"
            >
              <FiX size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AudioPlayer;
